import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '../apiClient';
import VoiceInput from './VoiceInput';

export default function ChatPhase({
  phase,
  selectedHA,
  onHAChange,
  departments,
  messages,
  setMessages,
  onStartConversation,
  onSummaryReady,
}) {
  const [inputValue, setInputValue] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Free-text input for resuming conversation after "Anpassen"
  const [resumeInput, setResumeInput] = useState('');

  // Voice-to-Text is encapsulated in <VoiceInput>; the refs let us stop
  // dictation on submit and flag manual edits so typing wins over the
  // live transcript. One instance per surface (main intake + custom answer).
  const mainVoiceRef = useRef(null);
  const customVoiceRef = useRef(null);
  const resumeVoiceRef = useRef(null);

  const inputRef = useRef(null);
  const chatEndRef = useRef(null);
  const customInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  // Focus management
  useEffect(() => {
    if (phase === 'input') {
      inputRef.current?.focus();
    }
  }, [phase]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentQuestion]);

  useEffect(() => {
    if (showCustomInput) {
      customInputRef.current?.focus();
    }
  }, [showCustomInput]);

  // When returning from summary (Anpassen), focus the resume input
  useEffect(() => {
    if (phase === 'conversation' && messages.length > 0 && !currentQuestion && !isLoading) {
      resumeInputRef.current?.focus();
    }
  }, [phase, messages.length, currentQuestion, isLoading]);

  // Send message to the conversation API
  const sendToChat = useCallback(async (allMessages) => {
    setIsLoading(true);
    setError(null);
    setShowCustomInput(false);
    setCustomInput('');

    try {
      const res = await apiFetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: allMessages }),
      });
      const data = await res.json();

      if (data.error) throw new Error(data.error);

      if (data.type === 'summary') {
        // AI returned summary -> transition to Phase 3
        onSummaryReady(data, allMessages);
      } else if (data.type === 'question') {
        // AI asking another question — stamp when it reached the user
        setCurrentQuestion({ ...data, receivedAt: Date.now() });
      }
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message);
    }
    setIsLoading(false);
  }, [onSummaryReady]);

  // Phase 1: Initial submission
  const handleInitialSubmit = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    // Stop voice recording on send
    mainVoiceRef.current?.stop();

    const initialMessages = [
      { role: 'user', content: text, ts: Date.now() },
    ];

    setMessages(initialMessages);
    setInputValue('');
    onStartConversation(initialMessages);

    // Start conversation
    await sendToChat(initialMessages);
  }, [inputValue, isLoading, setMessages, onStartConversation, sendToChat]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInitialSubmit();
    }
  }, [handleInitialSubmit]);

  // Phase 2: User selects an option
  const handleOptionClick = useCallback(async (option) => {
    if (isLoading) return;

    const { receivedAt, ...question } = currentQuestion;
    const assistantMessage = {
      role: 'assistant',
      content: JSON.stringify(question),
      ts: receivedAt ?? Date.now(),
    };
    const userMessage = { role: 'user', content: option.label, ts: Date.now() };

    const updatedMessages = [...messages, assistantMessage, userMessage];
    setMessages(updatedMessages);
    setCurrentQuestion(null);

    await sendToChat(updatedMessages);
  }, [isLoading, currentQuestion, messages, setMessages, sendToChat]);

  // Phase 2: User submits custom input
  const handleCustomSubmit = useCallback(async () => {
    const text = customInput.trim();
    if (!text || isLoading) return;
    customVoiceRef.current?.stop();

    const { receivedAt, ...question } = currentQuestion;
    const assistantMessage = {
      role: 'assistant',
      content: JSON.stringify(question),
      ts: receivedAt ?? Date.now(),
    };
    const userMessage = { role: 'user', content: text, ts: Date.now() };

    const updatedMessages = [...messages, assistantMessage, userMessage];
    setMessages(updatedMessages);
    setCurrentQuestion(null);
    setShowCustomInput(false);
    setCustomInput('');

    await sendToChat(updatedMessages);
  }, [customInput, isLoading, currentQuestion, messages, setMessages, sendToChat]);

  const handleCustomKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
  }, [handleCustomSubmit]);

  // FIX 6.3.1: Resume conversation after "Anpassen" — user can type freely
  const handleResumeSubmit = useCallback(async () => {
    const text = resumeInput.trim();
    if (!text || isLoading) return;
    resumeVoiceRef.current?.stop();

    const userMessage = { role: 'user', content: text, ts: Date.now() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setResumeInput('');

    await sendToChat(updatedMessages);
  }, [resumeInput, isLoading, messages, setMessages, sendToChat]);

  const handleResumeKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleResumeSubmit();
    }
  }, [handleResumeSubmit]);

  // ──────── PHASE 1: INPUT ────────
  if (phase === 'input') {
    return (
      <div className="input-container">
        <div className="input-window">
          <div className="input-hero">
            <h2>Welches Ergebnis möchtest du mit KI unterstützen?</h2>
            <p className="outlook-prompt">
              Öffne deinen Outlook-Kalender. Schau dir deine wichtigsten Termine der letzten 4 Wochen an. Für welchen hast du am meisten vorbereitet, nachbereitet oder Informationen zusammengesucht?
            </p>
          </div>

          {/* HA selector removed - no "Hauptabteilung" step */}
          {false && (
          <div className="ha-selector">
            <label className="ha-label">Deine Hauptabteilung</label>
            <select
              className="ha-dropdown"
              value={selectedHA}
              onChange={e => onHAChange(e.target.value)}
            >
              <option value="">-- Wähle deine HA --</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>
                  {d.name} {d.org_unit ? `\u2014 ${d.org_unit}` : ''}
                </option>
              ))}
            </select>
            {selectedHA && (
              <span className="ha-hint">
                Ausgewählte Hauptabteilung: {selectedHA}
              </span>
            )}
          </div>
          )}

          <div className="input-box-wrapper">
            <div className="input-box">
              <textarea
                ref={inputRef}
                className="process-input"
                rows={4}
                placeholder="Beschreib in eigenen Worten, was du regelmäßig tust und was dich dabei am meisten Zeit kostet..."
                value={inputValue}
                onChange={e => { setInputValue(e.target.value); mainVoiceRef.current?.markManualEdit(); }}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <div className="input-footer">
                <VoiceInput ref={mainVoiceRef} value={inputValue} setValue={setInputValue} />
                <button
                  className="btn btn-generate"
                  onClick={handleInitialSubmit}
                  disabled={!inputValue.trim() || isLoading}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">Fehler: {error}</div>
          )}
        </div>
      </div>
    );
  }

  // ──────── PHASE 2: CONVERSATION ────────
  // Determine if we're returning from "Anpassen" (have messages, no currentQuestion, not loading)
  const isResuming = messages.length > 0 && !currentQuestion && !isLoading;

  return (
    <div className="chat-container">
      <div className="chat-window">
        {/* Message history */}
        <div className="chat-messages">
          {messages.map((msg, i) => {
            if (msg.role === 'user') {
              return (
                <div key={i} className="chat-bubble user">
                  <div className="bubble-content">{msg.content}</div>
                </div>
              );
            }
            // Parse assistant message for display
            if (msg.role === 'assistant') {
              try {
                const parsed = JSON.parse(msg.content);
                return (
                  <div key={i} className="chat-bubble assistant">
                    <div className="bubble-content">{parsed.message}</div>
                  </div>
                );
              } catch {
                return (
                  <div key={i} className="chat-bubble assistant">
                    <div className="bubble-content">{msg.content}</div>
                  </div>
                );
              }
            }
            return null;
          })}

          {/* Current question from AI */}
          {currentQuestion && !isLoading && (
            <div className="chat-bubble assistant active-question">
              <div className="bubble-content">{currentQuestion.message}</div>

              {/* Progress dots */}
              {currentQuestion.questionNumber && currentQuestion.totalExpected && (
                <div className="question-progress">
                  <span className="progress-text">
                    Frage {currentQuestion.questionNumber} von {currentQuestion.totalExpected}
                  </span>
                  <div className="progress-dots">
                    {Array.from({ length: currentQuestion.totalExpected }, (_, i) => (
                      <div
                        key={i}
                        className={`progress-dot ${i < currentQuestion.questionNumber ? 'filled' : ''} ${i === currentQuestion.questionNumber - 1 ? 'current' : ''}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Option buttons */}
              <div className="option-buttons">
                {currentQuestion.options?.map((opt) => (
                  <button
                    key={opt.id}
                    className="option-btn"
                    onClick={() => handleOptionClick(opt)}
                  >
                    <span className="option-label">{opt.label}</span>
                    {opt.description && (
                      <span className="option-desc">{opt.description}</span>
                    )}
                  </button>
                ))}

                {currentQuestion.allowCustom && !showCustomInput && (
                  <button
                    className="option-btn custom-trigger"
                    onClick={() => setShowCustomInput(true)}
                  >
                    <span className="option-label">Sonstiges...</span>
                    <span className="option-desc">Eigene Antwort eingeben</span>
                  </button>
                )}
              </div>

              {/* Custom input */}
              {showCustomInput && (
                <>
                  <div className="custom-input-wrapper">
                    <input
                      ref={customInputRef}
                      type="text"
                      className="custom-input"
                      placeholder="Deine Antwort..."
                      value={customInput}
                      onChange={e => { setCustomInput(e.target.value); customVoiceRef.current?.markManualEdit(); }}
                      onKeyDown={handleCustomKeyDown}
                    />
                    <button
                      className="btn btn-send-custom"
                      onClick={handleCustomSubmit}
                      disabled={!customInput.trim()}
                    >
                      Senden
                    </button>
                  </div>
                  <VoiceInput ref={customVoiceRef} value={customInput} setValue={setCustomInput} className="custom-voice" />
                </>
              )}
            </div>
          )}

          {/* FIX 6.3.1: Resume input when returning from summary */}
          {isResuming && (
            <div className="chat-bubble assistant active-question">
              <div className="bubble-content">
                Was möchtest du ändern? Du kannst weitere Details ergänzen oder einfach "passt" schreiben, wenn alles stimmt.
              </div>
              <div className="custom-input-wrapper" style={{ marginTop: '12px' }}>
                <input
                  ref={resumeInputRef}
                  type="text"
                  className="custom-input"
                  placeholder="Änderung beschreiben oder 'passt' eingeben..."
                  value={resumeInput}
                  onChange={e => { setResumeInput(e.target.value); resumeVoiceRef.current?.markManualEdit(); }}
                  onKeyDown={handleResumeKeyDown}
                />
                <button
                  className="btn btn-send-custom"
                  onClick={handleResumeSubmit}
                  disabled={!resumeInput.trim()}
                >
                  Senden
                </button>
              </div>
              <VoiceInput ref={resumeVoiceRef} value={resumeInput} setValue={setResumeInput} className="custom-voice" />
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="chat-bubble assistant loading-bubble">
              <div className="typing-indicator">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {error && (
        <div className="chat-error">Fehler: {error}</div>
      )}
    </div>
  );
}
